/**
 * ZKNL motion — native Custom Elements for scroll-reveals, animated counters
 * and the sticky-scroll storytelling section.
 *
 * Design rule: content is always fully visible in the light DOM before this
 * script runs (server-rendered fallback). This file only ever *adds*
 * animation on top of already-visible content — it never hides content that
 * would stay hidden if JS fails to load/execute.
 *
 * Loaded via `<script src="{{ 'zknl-motion.js' | asset_url }}" defer="defer">`
 * from multiple sections. Every `customElements.define` call is guarded with
 * `customElements.get(...)` so re-parsing this file from more than one
 * <script> tag on the same page never throws (Dawn convention, see
 * cart-disclosure-modal.js).
 */

function zknlPrefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/* ---- <zknl-reveal> — fade/slide-in on scroll ---------------------------- */
if (!customElements.get('zknl-reveal')) {
  class ZknlReveal extends HTMLElement {
    connectedCallback() {
      if (this._zknlInit) return;
      this._zknlInit = true;

      if (zknlPrefersReducedMotion() || !('IntersectionObserver' in window)) {
        // No animation — content is already visible in the light DOM.
        return;
      }

      this.style.opacity = '0';
      this.style.transform = 'translateY(28px)';
      this.style.transition = 'opacity .9s var(--ease-out, ease-out), transform .9s var(--ease-out, ease-out)';

      const reveal = () => {
        this.style.opacity = '';
        this.style.transform = '';
      };

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal();
              io.unobserve(this);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -8% 0px' }
      );
      io.observe(this);

      // No timed fallback here: the opacity:0/translateY hidden state above is
      // only ever applied by this same branch (guarded by the
      // IntersectionObserver-availability check just above), so whenever the
      // content is hidden, IntersectionObserver is guaranteed to be present
      // and `reveal()` fires as soon as the element intersects. A timer would
      // instead force-reveal below-the-fold content before the user scrolls,
      // defeating the scroll-reveal effect.
    }
  }
  customElements.define('zknl-reveal', ZknlReveal);
}

/* ---- <zknl-counter> — animated number counter --------------------------- */
if (!customElements.get('zknl-counter')) {
  class ZknlCounter extends HTMLElement {
    connectedCallback() {
      if (this._zknlInit) return;
      this._zknlInit = true;

      const targetAttr = this.dataset.target;
      if (!targetAttr) return; // no target — leave server-rendered text alone

      const target = parseFloat(targetAttr.replace(',', '.'));
      if (Number.isNaN(target)) return;

      const suffix = this.dataset.suffix || '';
      const decimals = (targetAttr.split('.')[1] || '').length;
      const fallbackText = this.textContent;

      if (zknlPrefersReducedMotion() || !('IntersectionObserver' in window)) {
        // No animation — the end value is already the server-rendered text.
        return;
      }

      const formatter =
        typeof Intl !== 'undefined' && Intl.NumberFormat
          ? new Intl.NumberFormat('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
          : null;

      const format = (value) => (formatter ? formatter.format(value) : value.toFixed(decimals)) + suffix;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(this);

            const duration = 1400;
            const start = performance.now();
            const tick = (now) => {
              const progress = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - progress, 3);
              this.textContent = format(target * eased);
              if (progress < 1) {
                window.requestAnimationFrame(tick);
              } else {
                this.textContent = fallbackText; // restore exact authored text
              }
            };
            window.requestAnimationFrame(tick);
          });
        },
        { threshold: 0.5 }
      );
      io.observe(this);

      // No timed fallback here: `textContent` is already `fallbackText`
      // (the server-rendered value) until the observer fires, so there is no
      // stuck-at-zero state to correct — same reasoning as <zknl-reveal>.
    }
  }
  customElements.define('zknl-counter', ZknlCounter);
}

/* ---- <zknl-sticky-story> — sticky-scroll image/step crossfade ---------- */
if (!customElements.get('zknl-sticky-story')) {
  class ZknlStickyStory extends HTMLElement {
    connectedCallback() {
      if (this._zknlInit) return;
      this._zknlInit = true;

      const steps = [...this.querySelectorAll('[data-story-step]')];
      const images = [...this.querySelectorAll('[data-story-image]')];
      if (
        !steps.length ||
        !images.length ||
        !('IntersectionObserver' in window) ||
        zknlPrefersReducedMotion()
      ) {
        // Sticky positioning is pure CSS (works without JS); only the
        // image/step crossfade needs JS. The first image is already
        // marked is-active server-side, so content stays visible. Reduced-
        // motion users get the same server-rendered state with no crossfade.
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const i = entry.target.dataset.storyStep;
            steps.forEach((step) => step.classList.toggle('is-current', step.dataset.storyStep === i));
            // Not every step block has its own image (data-story-image is
            // only emitted for blocks with an image set). When the current
            // step has no matching image, leave the previously active image
            // in place instead of toggling every image to inactive — that
            // would blank the media panel for a mid-sequence, image-less step.
            const match = images.find((img) => img.dataset.storyImage === i);
            if (match) {
              images.forEach((img) => img.classList.toggle('is-active', img === match));
            }
          });
        },
        { threshold: 0.55 }
      );
      steps.forEach((step) => io.observe(step));
    }
  }
  customElements.define('zknl-sticky-story', ZknlStickyStory);
}

/* ---- [data-zknl-urgency] — live countdown naar de bestel-cutoff ------------
 * Server rendert de statische zin ("Bestel voor 12:00 …") als no-JS-fallback.
 * Met JS tikt hier een countdown naar de eerstvolgende cutoff op een werkdag.
 * In het weekend blijft de statische tekst staan (morgen-bezorgd klopt dan niet). */
(function () {
  function initUrgency() {
    var els = document.querySelectorAll('[data-zknl-urgency]:not([data-zknl-urgency-init])');
    if (!els.length) return;
    els.forEach(function (el) {
      el.setAttribute('data-zknl-urgency-init', '');
      var textEl = el.querySelector('[data-zknl-urgency-text]');
      var tmpl = el.getAttribute('data-countdown-template');
      var cutoff = parseInt(el.getAttribute('data-cutoff-hour'), 10);
      if (!textEl || !tmpl || isNaN(cutoff)) return;
      var fallbackHTML = textEl.innerHTML; // statische server-zin ("Bestel voor 12:00 …")

      function tick() {
        var now = new Date();
        var day = now.getDay(); // 0=zo, 6=za
        if (day === 0 || day === 6) return; // weekend: statische fallback laten staan
        if (now.getHours() >= cutoff) {
          // Ná de cutoff geen aftellende timer meer (die zou naar mórgen tellen
          // en "morgen bezorgd" beloven wat niet klopt) — toon de statische zin.
          textEl.innerHTML = fallbackHTML;
          return;
        }
        var target = new Date(now);
        target.setHours(cutoff, 0, 0, 0);
        var s = Math.floor((target - now) / 1000);
        var h = Math.floor(s / 3600);
        var m = Math.floor((s % 3600) / 60);
        var sec = s % 60;
        var stamp = (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m) + ':' + (sec < 10 ? '0' + sec : sec);
        textEl.innerHTML = tmpl.replace('@@', '<b>' + stamp + '</b>');
      }
      tick();
      setInterval(tick, 1000);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUrgency);
  } else {
    initUrgency();
  }
  document.addEventListener('shopify:section:load', initUrgency);
})();

/* ---- .reveal / .reveal-line / .clip-reveal (CSS-class scroll-reveal) -------
 * De geporte prototype-markup gebruikt naast <zknl-reveal> ook de CSS-class
 * `reveal`. zknl.css verbergt die met `.js .reveal{opacity:0}` en verwacht dat
 * JS de klasse `.in` toevoegt zodra het element in beeld komt. Zonder deze
 * observer blijven al die elementen permanent onzichtbaar. Met veiligheids-
 * timeout: content mag NOOIT verborgen blijven, ook niet als de observer faalt. */
(function () {
  var SELECTOR = '.reveal, .reveal-line, .clip-reveal';
  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var io = null;
  if (!reduceMotion() && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  }

  function attach(node) {
    if (node._zknlRevealed) return;
    node._zknlRevealed = true;
    if (!io) { node.classList.add('in'); return; }
    io.observe(node);
    // Vangnet per element: onthul alles wat 2.6s na registratie nog niet
    // getriggerd is (programmatische scroll, printen, observer-miss).
    // Zichtbaarheid > animatie. Per element i.p.v. één globale timer, zodat
    // ook laat geïnjecteerde content (product-recommendations, quick-add,
    // cart drawer) altijd zijn eigen vangnet krijgt.
    setTimeout(function () { node.classList.add('in'); }, 2600);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTOR)) attach(root);
    [].slice.call(root.querySelectorAll(SELECTOR)).forEach(attach);
  }

  function run() { scan(document.body || document.documentElement); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Her-scan na Shopify section-rerender (Theme Editor / dynamische secties).
  document.addEventListener('shopify:section:load', run);
  // Laat-geïnjecteerde content (product-recommendations fetcht zijn grid async,
  // predictive search, cart drawer): vang elke DOM-toevoeging met .reveal-nodes.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        [].slice.call(m.addedNodes).forEach(function (n) {
          if (n.nodeType === 1) scan(n);
        });
      });
    });
    var start = function () { mo.observe(document.body, { childList: true, subtree: true }); };
    if (document.body) { start(); } else { document.addEventListener('DOMContentLoaded', start); }
  }
})();
